import React, { useState, useEffect } from "react";
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
import axios from "axios";
import SideNavbar from "./helpers/SideNavbar";
import { Link, useNavigate } from 'react-router-dom';
import { useLocation } from "react-router-dom";
import Swal from 'sweetalert2';
import { useFetchSubscriptionPlans, useFetchLocalizedPlans } from "../apis/payment";
import { FaArrowLeft, FaCheckCircle, FaCreditCard, FaLock, FaShieldAlt } from 'react-icons/fa';
import { Form, Button, Alert } from 'react-bootstrap';
import '../assets/payment-form.css';

const StripePayment = () => {
  const stripe = useStripe();
  const elements = useElements();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const navigate = useNavigate();

  const { amount: initialAmount, type, plan, currency: stateCurrency, priceId: statePriceId } = location.state || {};
  const countryCode = localStorage.getItem("CountryCode") || "US";

  const [amount, setAmount] = useState(initialAmount);
  const [isRecurring, setIsRecurring] = useState(true); // Default to subscription (recurring)
  const [autoRenew, setAutoRenew] = useState(true); // Default to auto-renew enabled
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const userId = localStorage.getItem("UserID");
  const userName = localStorage.getItem("UserName") || "No Name Provided";
  const userEmail = localStorage.getItem("UserEmail") || "No Email Provided";

  const [startDate, setStartDate] = useState(() => new Date().toISOString());
  const { data: plans, isLoading } = useFetchSubscriptionPlans();
  const { data: localized } = useFetchLocalizedPlans(userId);

  const currency = stateCurrency || localized?.currency || 'USD';
  const currencySymbol = currency === 'INR' ? '₹' : '$';
  const effectivePriceId = statePriceId || (plan === 'Annually' ? localized?.priceIds?.yearly : localized?.priceIds?.monthly);

  const [endDate, setEndDate] = useState(() => {
    const date = new Date();
    if (plan === "Monthly") {
      date.setMonth(date.getMonth() + 1);
    } else if (plan === "Annually") {
      date.setFullYear(date.getFullYear() + 1);
    }
    return date.toISOString();
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    handleResize(); // Check on mount
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const savedEndDate = new Date(localStorage.getItem("subscriptionEndDate"));
    const today = new Date();

    if (savedEndDate > today) {
      Swal.fire({
        title: "Active Subscription",
        text: `You already have a subscription valid until ${savedEndDate.toDateString()}. Do you want to renew it now?`,
        icon: "info",
        showCancelButton: true,
        confirmButtonText: "Yes, Renew",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#2e3192",
        cancelButtonColor: "#f44336"
      }).then((result) => {
        if (result.isConfirmed) {
          const newStart = savedEndDate;
          const newEnd = new Date(newStart);
          if (plan === "Monthly") {
            newEnd.setMonth(newEnd.getMonth() + 1);
          } else if (plan === "Annually") {
            newEnd.setFullYear(newEnd.getFullYear() + 1);
          }
          setStartDate(newStart.toISOString());
          setEndDate(newEnd.toISOString());
        } else {
          navigate("/subscription");
        }
      });
    }
  }, []);

  const transformPlansToPriceMap = (plans, type, plan) => {
    const priceMap = {};

    plans.forEach(({ BrandingType, DurationType, PriceID }) => {
      if (!priceMap[BrandingType]) {
        priceMap[BrandingType] = {};
      }
      priceMap[BrandingType][DurationType] = PriceID;
    });

    return priceMap[type]?.[plan] || null;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setMessage("");

    if (!stripe || !elements) {
      setMessage("Stripe has not loaded yet.");
      setLoading(false);
      return;
    }

    const cardElement = elements.getElement(CardElement);

    const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card: cardElement,
      billing_details: {
        name: userName,
        email: userEmail,
      },
    });

    if (paymentMethodError) {
      setMessage(paymentMethodError.message);
      setLoading(false);
      return;
    }

    const paymentMethodId = paymentMethod.id;

    try {
      const amountInCents = Math.round(parseFloat(amount) * 100);
      if (isNaN(amountInCents) || amountInCents <= 0) {
        setMessage("Please enter a valid amount.");
        setLoading(false);
        return;
      }

      const endpoint = isRecurring
        ? `${process.env.REACT_APP_API_BASE_URL}/api/create-subscription`
        : `${process.env.REACT_APP_API_BASE_URL}/api/create-payment-intent`;

      const payload = isRecurring
        ? {
            userId,
            userName,
            userEmail,
            priceId: effectivePriceId || transformPlansToPriceMap(plans, type, plan),
            paymentMethodId,
            countryCode,
            autoRenew: autoRenew, // Include auto-renew preference
          }
        : {
            userId,
            userName,
            userEmail,
            amount: amountInCents,
          };

      const { data } = await axios.post(endpoint, payload);
      const { clientSecret, paymentIntentId, subscriptionId } = data;

      if (isRecurring && subscriptionId) {
        const { data: subscription_data } = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/check-subscription/${subscriptionId}`);
        if (!subscription_data || !subscription_data.status) {
          setMessage("Subscription creation failed. Please try again.");
          Swal.fire({ icon: "error", title: "Subscription Error", text: "Subscription creation failed. Please try again." });
          setLoading(false);
          return;
        }
        if (subscription_data.status === "active") {
          try {
            await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/save-subscription`, {
              userId,
              userName,
              userEmail,
              startDate,
              endDate,
              subscriptionType: plan,
              subscriptionId: subscriptionId,
            });

            Swal.fire({ 
              position: "center", 
              icon: "success", 
              title: "Payment Successful", 
              html: "Please logout and login again to get all functionalities.",
              showConfirmButton: true,
              confirmButtonText: "OK",
              confirmButtonColor: "#2e3192"
            }).then(() => {
              localStorage.setItem("showPostPaymentAlert", "false");
              // Refresh feature limits data to update subscription status
              if (window.refreshFeatureUsage) {
                window.refreshFeatureUsage();
              }
              navigate("/subscription");
            });
          } catch (saveError) {
            console.error("Save payment error:", saveError);
            setMessage("Payment was successful, but we couldn't save your subscription. Please contact support.");
            Swal.fire({ icon: "warning", title: "Subscription Issue", text: "Payment was successful, but we couldn't save your subscription. Please contact support." });
          }
        } else {
          Swal.fire({ icon: "warning", title: `Subscription ${subscription_data.status}`, text: "Payment was successful, but we couldn't save your subscription. Please contact support." });
        }
      } else {
        const paymentResult = await stripe.confirmCardPayment(clientSecret, { payment_method: { card: cardElement } });

        if (paymentResult.error) {
          setMessage(paymentResult.error.message);
          Swal.fire({ icon: "error", title: "Payment Failed", text: paymentResult.error.message });
        } else {
          setMessage("Payment successful!");
          setAmount("");

          try {
            await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/save-payment`, {
              paymentIntentId,
              userId,
              userName,
              userEmail,
              startDate,
              endDate,
              subscriptionType: plan,
              subscriptionId: plan === "Monthly" ? 1 : plan === "Annually" ? 2 : null,
            });

            Swal.fire({ 
              position: "center", 
              icon: "success", 
              title: "Payment Successful", 
              html: "Please logout and login again to get all functionalities.",
              showConfirmButton: true,
              confirmButtonText: "OK",
              confirmButtonColor: "#2e3192"
            }).then(() => {
              localStorage.setItem("showPostPaymentAlert", "true");
              // Refresh feature limits data to update subscription status
              if (window.refreshFeatureUsage) {
                window.refreshFeatureUsage();
              }
              navigate("/subscription");
            });
          } catch (saveError) {
            console.error("Save payment error:", saveError);
            setMessage("Payment was successful, but we couldn't save your subscription. Please contact support.");
            Swal.fire({ icon: "warning", title: "Subscription Issue", text: "Payment was successful, but we couldn't save your subscription. Please contact support." });
          }
        }
      }
    } catch (error) {
      console.error("Payment error:", error);
      setMessage("Something went wrong. Please try again later.");
      Swal.fire({ icon: "error", title: "Payment Failed", text: "Something went wrong. Please try again later." });
    }

    setLoading(false);
  };

  return (
    <div className="home-container">
      <style>{`
        .home-container {
          overflow-y: visible !important;
          height: auto !important;
          min-height: 100vh !important;
          position: relative !important;
        }
        #content {
          overflow-y: visible !important;
          height: auto !important;
          min-height: 100vh !important;
          position: relative !important;
        }
        .payment-form-container {
          overflow: visible !important;
          position: relative !important;
        }
        body {
          overflow-y: auto !important;
          height: auto !important;
        }
        html {
          overflow-y: auto !important;
          height: auto !important;
        }
      `}</style>
      <style>{`
        @media (max-width: 767px) {
          .payment-form-container {
            padding: 20px 15px !important;
          }
          .payment-label-container {
            min-width: auto !important;
            width: 100% !important;
            margin-bottom: 8px !important;
          }
          .payment-label-icon {
            font-size: 14px !important;
          }
          .payment-label-text {
            font-size: 14px !important;
          }
          .payment-input {
            padding: 10px 12px !important;
            font-size: 14px !important;
          }
          .payment-card-wrapper {
            width: 100%;
          }
          .payment-card-wrapper .__PrivateStripeElement {
            font-size: 16px !important;
          }
          .payment-card-wrapper input {
            font-size: 16px !important;
            padding: 10px 12px !important;
          }
          .payment-card-wrapper input::placeholder {
            font-size: 14px !important;
          }
          .auto-renew-container {
            padding: 15px !important;
            border-radius: 12px !important;
          }
          .auto-renew-toggle {
            width: 48px !important;
            height: 28px !important;
            margin-right: 12px !important;
            margin-bottom: 8px !important;
          }
          .auto-renew-toggle-circle {
            width: 22px !important;
            height: 22px !important;
          }
          .auto-renew-toggle-circle.enabled {
            left: 23px !important;
          }
          .auto-renew-title {
            font-size: 14px !important;
          }
          .auto-renew-text {
            font-size: 12px !important;
          }
          .auto-renew-benefits {
            font-size: 11px !important;
          }
          .payment-button {
            padding: 12px 20px !important;
            font-size: 16px !important;
            min-height: 48px !important;
          }
          .payment-secure-text {
            font-size: 12px !important;
          }
        }
        @media (min-width: 768px) {
          .payment-label-container {
            min-width: 140px !important;
          }
          .payment-label-icon {
            font-size: 16px !important;
          }
          .payment-label-text {
            font-size: 16px !important;
          }
          .payment-input {
            padding: 12px 16px !important;
            font-size: 16px !important;
          }
          .payment-card-wrapper .__PrivateStripeElement {
            font-size: 18px !important;
          }
          .payment-card-wrapper input {
            font-size: 18px !important;
            padding: 12px 16px !important;
          }
          .auto-renew-toggle {
            width: 56px !important;
            height: 32px !important;
            margin-right: 16px !important;
          }
          .auto-renew-toggle-circle {
            width: 26px !important;
            height: 26px !important;
          }
          .auto-renew-toggle-circle.enabled {
            left: 27px !important;
          }
        }
      `}</style>
      <SideNavbar>
        <div className="card shadow-sm p-2 p-md-3 mt-2 mt-md-3 mb-4" style={{ border: "none", backgroundColor: "#fff", maxWidth: '100%', overflowX: 'hidden', minHeight: 'auto', position: 'relative' }}>
          <div className="card-header" style={{ border: "none", backgroundColor: "#fff", padding: '12px 8px' }}>
            <Link to="/subscription">
              <h5 className="welcome-text" style={{ fontSize: 'clamp(16px, 4vw, 20px)', color: '#2e3192', textAlign: 'left', margin: 0 }}>
                <i className="fa fa-arrow-left d-none d-md-inline" style={{ marginRight: '30px' }} aria-hidden="true"></i>
                <i className="fa fa-arrow-left d-md-none" style={{ marginRight: '10px' }} aria-hidden="true"></i>
                Payment
              </h5>
            </Link>
          </div>
          <hr style={{ margin: '8px 0' }} />

          <div className="container-fluid px-2 px-md-3">
            <div className="row mt-2">
              <div className="col-12 col-lg-8 col-xl-8 col-md-8">
                <div className="payment-form-container" style={{ 
                  background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                  borderRadius: '12px',
                  padding: '20px 15px',
                  border: '1px solid #e0e0e0',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}
                >
                  <div className="row">
                    <div className="col-12">
                      <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center mb-3">
                        <div className="payment-label-container mb-2 mb-md-0 me-md-3" style={{ display: 'flex', alignItems: 'center' }}>
                          <FaCreditCard className="payment-label-icon me-2" style={{ color: '#2e3192' }} />
                          <Form.Label className="payment-label-text fw-bold text-dark mb-0">
                            Email:
                          </Form.Label>
                        </div>
                        <div className="flex-grow-1 w-100">
                          <Form.Control 
                            type="email" 
                            value={userEmail} 
                            disabled 
                            className="payment-input"
                            style={{
                              backgroundColor: '#f8f9fa',
                              border: '2px solid #e9ecef',
                              borderRadius: '8px'
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-12">
                      <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center mb-3">
                        <div className="payment-label-container mb-2 mb-md-0 me-md-3" style={{ display: 'flex', alignItems: 'center' }}>
                          <FaCheckCircle className="payment-label-icon me-2" style={{ color: '#28a745' }} />
                          <Form.Label className="payment-label-text fw-bold text-dark mb-0">
                            Name:
                          </Form.Label>
                        </div>
                        <div className="flex-grow-1 w-100">
                          <Form.Control 
                            type="text" 
                            value={userName} 
                            disabled 
                            className="payment-input"
                            style={{
                              backgroundColor: '#f8f9fa',
                              border: '2px solid #e9ecef',
                              borderRadius: '8px'
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-12">
                      <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center mb-3">
                        <div className="payment-label-container mb-2 mb-md-0 me-md-3" style={{ display: 'flex', alignItems: 'center' }}>
                          <span className="payment-label-icon me-2">💰</span>
                          <Form.Label className="payment-label-text fw-bold text-dark mb-0">
                            Amount:
                          </Form.Label>
                        </div>
                        <div className="flex-grow-1 w-100">
                          <Form.Control 
                            type="text" 
                            value={`${currencySymbol}${amount}`} 
                            disabled 
                            className="payment-input"
                            style={{
                              backgroundColor: '#f8f9fa',
                              border: '2px solid #e9ecef',
                              borderRadius: '8px'
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <form onSubmit={handleSubmit}>
                    <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center mb-4">
                      <div className="payment-label-container mb-2 mb-md-0 me-md-3" style={{ display: 'flex', alignItems: 'center' }}>
                        <FaCreditCard className="payment-label-icon me-2" style={{ color: '#2e3192' }} />
                        <Form.Label className="payment-label-text fw-bold text-dark mb-0">
                          Card Number:
                        </Form.Label>
                      </div>
                      <div className="flex-grow-1 w-100">
                        <div className="payment-card-wrapper">
                          <CardElement
                            options={{
                              style: {
                                base: {
                                  fontSize: isMobile ? '16px' : '18px',
                                  color: '#2e3192',
                                  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                  fontWeight: '500',
                                  backgroundColor: '#f8f9fa',
                                  border: '2px solid #e9ecef',
                                  borderRadius: '8px',
                                  padding: isMobile ? '10px 12px' : '12px 16px',
                                  '::placeholder': {
                                    color: '#6c757d',
                                    fontWeight: '400',
                                    fontSize: isMobile ? '14px' : '16px'
                                  },
                                  '::selection': {
                                    backgroundColor: '#e3f2fd'
                                  }
                                },
                                invalid: {
                                  color: '#dc3545',
                                  iconColor: '#dc3545',
                                  border: '2px solid #dc3545'
                                },
                                complete: {
                                  color: '#28a745',
                                  iconColor: '#28a745',
                                  border: '2px solid #28a745'
                                }
                              },
                              hidePostalCode: countryCode === "IN"
                            }} 
                          />
                        </div>
                      </div>
                    </div>
                    <div className="mt-2">
                      <small className="text-muted payment-secure-text">
                        <FaLock className="me-1" style={{ color: '#28a745' }} />
                        Your card details are encrypted and secure. We never store your card information.
                      </small>
                    </div>

                    {/* Auto-Renew Toggle - Modern Design */}
                    <div 
                      className="mt-4 mb-4 auto-renew-container"
                      style={{
                        padding: '20px',
                        backgroundColor: autoRenew ? '#f0f9ff' : '#fafafa',
                        borderRadius: '16px',
                        border: `2px solid ${autoRenew ? '#2e3192' : '#e5e7eb'}`,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        overflow: 'hidden',
                        boxShadow: autoRenew 
                          ? '0 4px 16px rgba(46, 49, 146, 0.1)' 
                          : '0 2px 8px rgba(0, 0, 0, 0.05)'
                      }}
                    >
                      {/* Background gradient effect when enabled */}
                      {autoRenew && (
                        <div
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'linear-gradient(135deg, rgba(46, 49, 146, 0.05) 0%, rgba(46, 49, 146, 0.02) 100%)',
                            zIndex: 0
                          }}
                        />
                      )}
                      
                      <div className="d-flex align-items-center flex-wrap" style={{ position: 'relative', zIndex: 1 }}>
                        {/* Custom Toggle Switch */}
                        <div
                          role="switch"
                          tabIndex={0}
                          aria-label={autoRenew ? "Disable auto-renewal" : "Enable auto-renewal"}
                          aria-checked={autoRenew}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setAutoRenew(!autoRenew);
                            }
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setAutoRenew(!autoRenew);
                          }}
                          className="auto-renew-toggle"
                          style={{
                            borderRadius: '16px',
                            backgroundColor: autoRenew ? '#2e3192' : '#d1d5db',
                            position: 'relative',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            cursor: 'pointer',
                            flexShrink: 0,
                            boxShadow: autoRenew 
                              ? '0 4px 12px rgba(46, 49, 146, 0.3)' 
                              : '0 2px 4px rgba(0, 0, 0, 0.1)'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.05)';
                            e.currentTarget.style.boxShadow = autoRenew 
                              ? '0 6px 16px rgba(46, 49, 146, 0.4)' 
                              : '0 4px 8px rgba(0, 0, 0, 0.15)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.boxShadow = autoRenew 
                              ? '0 4px 12px rgba(46, 49, 146, 0.3)' 
                              : '0 2px 4px rgba(0, 0, 0, 0.1)';
                          }}
                        >
                          <div
                            className={`auto-renew-toggle-circle ${autoRenew ? 'enabled' : ''}`}
                            style={{
                              borderRadius: '50%',
                              backgroundColor: '#ffffff',
                              position: 'absolute',
                              top: '3px',
                              left: autoRenew ? '27px' : '3px',
                              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            {autoRenew && (
                              <FaCheckCircle style={{ color: '#2e3192', fontSize: '14px' }} />
                            )}
                          </div>
                        </div>

                        {/* Content */}
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center mb-1">
                            <div className="flex-grow-1">
                              <h6 
                                className="mb-0 fw-bold auto-renew-title"
                                style={{ 
                                  color: autoRenew ? '#2e3192' : '#1f2937',
                                  fontSize: '16px',
                                  transition: 'color 0.3s ease'
                                }}
                              >
                                Enable Auto-Renewal
                              </h6>
                              <p 
                                className="mb-0 mt-1 auto-renew-text d-none d-md-block"
                                style={{ 
                                  color: '#6b7280',
                                  fontSize: '13px',
                                  lineHeight: '1.5'
                                }}
                              >
                                Your subscription will automatically renew at the end of each billing period. You can cancel anytime from your account settings.
                              </p>
                              <p 
                                className="mb-0 mt-1 auto-renew-text d-md-none"
                                style={{ 
                                  color: '#6b7280',
                                  fontSize: '12px',
                                  lineHeight: '1.5'
                                }}
                              >
                                Your subscription will automatically renew at the end of each billing period.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Benefits when enabled */}
                      {autoRenew && (
                        <div 
                          className="mt-3 pt-3"
                          style={{ 
                            borderTop: '1px solid rgba(46, 49, 146, 0.1)',
                            position: 'relative',
                            zIndex: 1
                          }}
                        >
                          <div className="d-flex align-items-center flex-wrap">
                            <FaShieldAlt style={{ color: '#2e3192', fontSize: '14px', marginRight: '8px', flexShrink: 0 }} />
                            <small className="auto-renew-benefits d-none d-md-inline" style={{ color: '#4b5563', fontSize: '12px', lineHeight: '1.4' }}>
                              <strong>Benefits:</strong> Never miss a payment • Seamless service continuation • Easy cancellation anytime
                            </small>
                            <small className="auto-renew-benefits d-md-none" style={{ color: '#4b5563', fontSize: '11px', lineHeight: '1.4' }}>
                              <strong>Benefits:</strong> Never miss a payment • Easy cancellation
                            </small>
                          </div>
                        </div>
                      )}
                    </div>



                    <div className="d-grid">
                      <Button 
                        type="submit"
                        variant="primary" 
                        disabled={!stripe || loading}
                        size="lg"
                        className="payment-button"
                        style={{ 
                          backgroundColor: '#2e3192', 
                          borderColor: '#2e3192',
                          borderRadius: '10px',
                          padding: '15px 30px',
                          fontSize: '18px',
                          fontWeight: 'bold',
                          boxShadow: '0 4px 12px rgba(46, 49, 146, 0.3)',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Processing Payment...
                          </>
                        ) : (
                          <>
                            <FaLock className="me-2" />
                            Pay {currencySymbol}{amount} Securely
                          </>
                        )}
                      </Button>
                  </div>
                </form>

                {message && (
                    <Alert variant="info" className="mt-3" style={{
                      borderRadius: '8px',
                      border: 'none',
                      backgroundColor: '#d1ecf1',
                      color: '#0c5460'
                    }}>
                      <strong>Payment Status:</strong> {message}
                    </Alert>
                  )}
                  </div>
              </div>
            </div>
          </div>
        </div>
      </SideNavbar>
    </div>
  );
};

export default StripePayment;

